<!--
  SocialLinksEditor — the add/edit/remove UI for a list of social links.
  v-model is the array of { platform, url }. Each row: brand icon, A→Z
  platform dropdown (no emoji), URL/@handle field, open-↗, red delete.
-->
<template>
  <div class="form-group">
    <div class="social-head">
      <label>{{ label }} <span v-if="hint" class="optional">{{ hint }}</span></label>
      <button type="button" class="btn btn-outline btn-sm" @click="add">+ Add social</button>
    </div>
    <div v-for="(s, i) in model" :key="i" class="social-row">
      <span class="social-icon" :title="platformLabel(s.platform)">
        <svg v-if="platformSvg(s.platform)" viewBox="0 0 24 24" class="brand-svg" aria-hidden="true"><path :d="platformSvg(s.platform)" fill="currentColor" /></svg>
        <template v-else>{{ platformIcon(s.platform) }}</template>
      </span>
      <select :value="s.platform" class="filter-select" @change="updateField(i, 'platform', ($event.target as HTMLSelectElement).value)">
        <option v-for="p in SOCIAL_PLATFORMS_SORTED" :key="p.value" :value="p.value">{{ p.label }}</option>
      </select>
      <input :value="s.url" class="social-url" placeholder="URL or @handle" @input="updateField(i, 'url', ($event.target as HTMLInputElement).value)" />
      <a v-if="socialUrl(s.platform, s.url)" :href="socialUrl(s.platform, s.url)" target="_blank" rel="noopener" class="social-open" title="Open link">↗</a>
      <button type="button" class="social-del" title="Remove" @click="remove(i)">✕</button>
    </div>
    <p v-if="!model.length" class="muted">No socials yet.</p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  SOCIAL_PLATFORMS, SOCIAL_PLATFORMS_SORTED,
  platformIcon, platformLabel, platformSvg, socialUrl,
} from '@/lib/socialPlatforms'

type Social = { platform: string; url: string }
const props = withDefaults(defineProps<{
  modelValue: Social[]
  label?: string
  hint?: string
}>(), { label: 'Social links', hint: '' })

const emit = defineEmits<{ (e: 'update:modelValue', v: Social[]): void }>()
// Read-only view of the bound array. Edits go through updateField/add/remove,
// which EMIT a new array — never mutate items in place. In-place mutation breaks
// when the parent binds a computed/mapped array (e.g. a {platform,handle}↔{url}
// bridge): the mutation lands on a throwaway object and the parent's setter never
// runs, so the edit is silently lost. Emitting a fresh array always triggers it.
const model = computed(() => props.modelValue)

function updateField(i: number, key: keyof Social, value: string) {
  const next = props.modelValue.map((s, idx) => idx === i ? { ...s, [key]: value } : s)
  emit('update:modelValue', next)
}

function add() { emit('update:modelValue', [...props.modelValue, { platform: SOCIAL_PLATFORMS[0].value, url: '' }]) }
function remove(i: number) {
  const next = props.modelValue.slice()
  next.splice(i, 1)
  emit('update:modelValue', next)
}
</script>

<style scoped>
.social-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.4rem; }
.social-head label { font-size: 0.875rem; color: var(--text-secondary); }
.social-row { display: flex; gap: 0.5rem; align-items: center; margin-bottom: 0.5rem; }
.social-icon { font-size: 1.1rem; width: 1.5rem; text-align: center; flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center; color: var(--text-secondary); }
.brand-svg { width: 1.05rem; height: 1.05rem; display: block; }
.social-row .filter-select { flex: 0 0 150px; }
.social-url {
  flex: 1 1 auto; min-width: 0; padding: 0.5rem 0.65rem;
  border: 1px solid var(--border-default); border-radius: var(--radius-sm);
  background: var(--bg-input); color: var(--text-primary); font-size: 0.875rem;
}
.social-url:focus { outline: none; border-color: var(--border-focus); }
.social-open { color: var(--accent-text); text-decoration: none; font-size: 1rem; padding: 0 0.2rem; flex: 0 0 auto; }
.social-open:hover { text-decoration: underline; }
.social-del {
  flex: 0 0 auto; width: 1.6rem; height: 1.6rem; line-height: 1;
  background: transparent; border: 1px solid var(--border-default); border-radius: var(--radius-sm);
  color: var(--danger-text, #d9534f); cursor: pointer; font-size: 0.8rem;
  transition: background 0.12s, border-color 0.12s;
}
.social-del:hover { background: var(--danger-dim, rgba(217,83,79,0.14)); border-color: var(--danger-text, #d9534f); }
</style>
